import { useEffect, useState } from "react";
import { ShieldCheck, Globe, Trash2, Check, X, Plus } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

export function InternalIpPanel({ canManage }) {
  const [access, setAccess] = useState({ ip: "", whitelisted: true, allowlist: [], can_manage: false });
  const [requests, setRequests] = useState([]);
  const [newIp, setNewIp] = useState("");

  const load = () => api.get("/internal-access").then((r) => setAccess(r.data)).catch(() => {});
  const loadReqs = () => api.get("/internal-ip/requests").then((r) => setRequests(r.data || [])).catch(() => setRequests([]));
  useEffect(() => { load(); if (canManage) loadReqs(); }, [canManage]);

  const addIp = async (ip) => {
    try { await api.post("/internal-ip/add", { ip }); toast.success("IP whitelisted"); setNewIp(""); load(); }
    catch { toast.error("Could not add IP"); }
  };
  const removeIp = async (ip) => {
    try { await api.delete(`/internal-ip/${encodeURIComponent(ip)}`); toast.success("IP removed"); load(); }
    catch { toast.error("Could not remove IP"); }
  };
  const approveReq = async (rid) => { try { await api.post(`/internal-ip/requests/${rid}/approve`); toast.success("Approved & whitelisted"); load(); loadReqs(); } catch { toast.error("Failed"); } };
  const dismissReq = async (rid) => { try { await api.delete(`/internal-ip/requests/${rid}`); loadReqs(); } catch { toast.error("Failed"); } };
  const requestMine = async () => { try { await api.post("/internal-ip/request"); toast.success("Whitelist request sent to an admin"); } catch { toast.error("Failed"); } };

  return (
    <Card className="border-slate-200 shadow-sm p-6 space-y-4" data-testid="internal-ip-panel">
      <div>
        <div className="flex items-center gap-2 text-sm font-semibold text-slate-800"><ShieldCheck className="h-4 w-4 text-amber-500" /> Internal-Only apps — staff IP allow-list</div>
        <p className="mt-1 text-xs text-slate-400">Apps marked "Internal only" appear in the dashboard's Internal section and are visible only from these IPs/CIDRs. Empty = feature inactive (internal apps shown to all staff). Super-admins are always exempt.</p>
      </div>

      <div className="flex items-center justify-between rounded-lg border border-slate-200 bg-slate-50/60 px-4 py-3">
        <div className="flex items-center gap-2 text-sm">
          <Globe className="h-4 w-4 text-slate-400" />
          <span className="text-slate-600">Your current IP:</span>
          <span className="font-mono font-medium text-slate-800" data-testid="settings-my-ip">{access.ip || "…"}</span>
          {access.whitelisted ? <span className="rounded-full bg-green-50 px-2 py-0.5 text-[11px] font-medium text-green-700">whitelisted</span>
            : <span className="rounded-full bg-amber-50 px-2 py-0.5 text-[11px] font-medium text-amber-700">not whitelisted</span>}
        </div>
        {canManage
          ? <Button size="sm" variant="outline" onClick={() => addIp(access.ip)} data-testid="settings-whitelist-my-ip">Whitelist my IP</Button>
          : <Button size="sm" variant="outline" onClick={requestMine} data-testid="settings-request-my-ip">Request whitelist</Button>}
      </div>

      {canManage && (
        <>
          <div className="space-y-2">
            <Label>Allow-listed IPs / CIDRs</Label>
            {(access.allowlist || []).length === 0 && <p className="text-xs text-slate-400">No IPs yet — internal apps are visible to all staff.</p>}
            <div className="space-y-1.5">
              {(access.allowlist || []).map((ip) => (
                <div key={ip} className="flex items-center justify-between rounded-lg border border-slate-100 px-3 py-1.5" data-testid={`allowlist-row-${ip}`}>
                  <span className="font-mono text-sm text-slate-700">{ip}</span>
                  <button onClick={() => removeIp(ip)} data-testid={`allowlist-remove-${ip}`} className="text-slate-400 hover:text-rose-500"><Trash2 className="h-4 w-4" /></button>
                </div>
              ))}
            </div>
            <div className="flex items-center gap-2">
              <Input value={newIp} onChange={(e) => setNewIp(e.target.value)} placeholder="e.g. 203.0.113.5 or 198.51.100.0/24" data-testid="allowlist-add-input" />
              <Button size="sm" onClick={() => newIp.trim() && addIp(newIp.trim())} data-testid="allowlist-add-button"><Plus className="h-4 w-4" /> Add</Button>
            </div>
          </div>

          {requests.length > 0 && (
            <div className="space-y-2">
              <Label>Pending whitelist requests</Label>
              {requests.map((r) => (
                <div key={r.request_id} className="flex items-center justify-between rounded-lg border border-amber-100 bg-amber-50/50 px-3 py-2" data-testid={`ipreq-row-${r.request_id}`}>
                  <div className="text-xs"><span className="font-mono font-medium text-slate-700">{r.ip}</span><span className="text-slate-500"> · {r.user_name || r.user_email}</span></div>
                  <div className="flex items-center gap-1">
                    <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => approveReq(r.request_id)} data-testid={`ipreq-approve-${r.request_id}`}><Check className="h-4 w-4 text-green-600" /></Button>
                    <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => dismissReq(r.request_id)} data-testid={`ipreq-dismiss-${r.request_id}`}><X className="h-4 w-4 text-slate-500" /></Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </>
      )}
    </Card>
  );
}
